import React, { useMemo, useState } from 'react';
import { Box, Button, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';

import { columns } from './data';
import AddAdminModal from './AddAdminModal';
import ViewAdminModal from './ViewAdminModal';
import EditAdminModal from './EditAdminModal';
import DeleteAdminModal from './DeleteAdminModal';
import Table from '../../components/table';
import Topbar from '../../components/topbar';
import Searchbar from '../../components/searchbar';
import CustomSelect from '../../components/select';
import MainLayout from '../../components/mainlayout';
import useGetAdmins from '../../api/queries/useGetAdmins';
import useDeleteAdminMutation from '../../api/mutations/useDeleteAdminMutation';
import { SORT_NEWEST_FIRST } from '../../utils/constants';

const Users = () => {
  const {
    isOpen: isAddAdminOpen,
    onOpen: onAddAdminOpen,
    onClose: onAddAdminClose,
  } = useDisclosure();

  const {
    isOpen: isViewAdminOpen,
    onOpen: onViewAdminOpen,
    onClose: onViewAdminClose,
  } = useDisclosure();

  const {
    isOpen: isEditAdminOpen,
    onOpen: onEditAdminOpen,
    onClose: onEditAdminClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteAdminOpen,
    onOpen: onDeleteAdminOpen,
    onClose: onDeleteAdminClose,
  } = useDisclosure();

  const toast = useToast();
  const adminsQuery = useGetAdmins();
  const deleteAdminMutation = useDeleteAdminMutation({
    onSuccess: () => {
      toast({
        title: 'Employee deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteAdminClose();
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete employee',
        description: error?.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onDeleteAdminClose();
    },
  });
  const [sortOrder, setSortOrder] = useState(SORT_NEWEST_FIRST);
  const [search, setSearch] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const sorted = useMemo(() => {
    const raw = adminsQuery?.data?.data || [];

    // Filter only employees and owners and search
    const filtered = raw.filter(admin => {
      // Only show users with role "employee" or "owner"
      if (admin.authUser?.role !== 'employee' && admin.authUser?.role !== 'owner') return false;

      if (!search) return true;
      const firstName = admin.first_name?.toLowerCase() || '';
      const surname = admin.surname?.toLowerCase() || '';
      const email = admin.email_address?.toLowerCase() || '';
      return firstName.includes(search.toLowerCase()) ||
             surname.includes(search.toLowerCase()) ||
             email.includes(search.toLowerCase());
    });

    // Then sort
    const result = [...filtered].sort((a, b) => {
      const dateA = new Date(a?.createdAt || a?.date_created);
      const dateB = new Date(b?.createdAt || b?.date_created);

      if (sortOrder === SORT_NEWEST_FIRST) {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });

    return result;
  }, [adminsQuery?.data, sortOrder, search]);

  const handleViewAdmin = (admin) => {
    setSelectedAdmin(admin);
    onViewAdminOpen();
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    onEditAdminOpen();
  };

  const handleDeleteAdmin = (admin) => {
    setSelectedAdmin(admin);
    onDeleteAdminOpen();
  };

  const confirmDeleteAdmin = () => {
    deleteAdminMutation.mutate(selectedAdmin?._id);
  };

  return (
    <MainLayout h={'100%'} minH={'100vh'} paddingBottom={20}>
      <Box>
        <Topbar title={'USERS'} />
        <Flex
          bg={'#f8fbff'}
          padding={8}
          height={'100%'}
          gap={2}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Flex align={'center'}>
            <Searchbar onChange={setSearch} title={'Search'} />
          </Flex>
          <Flex justify={'space-between'} alignItems={'center'}>
            <Flex gap={3}>
              <CustomSelect
                label={'Sort'}
                onChange={setSortOrder}
                style={{ maxWidth: '200px' }}
                inputStyle={{
                  border: 'none',
                  background: '#fff',
                  color: '#898989',
                  fontSize: '14px',
                  fontFamily: 'PlusJakartaSansMedium',
                }}
              >
                <option value={'newest'}>Newest First</option>
                <option value={'oldest'}>Oldest First</option>
              </CustomSelect>
              <Button
                onClick={() => onAddAdminOpen()}
                w={'129px'}
                py={6}
                bg={'#456EFF'}
                borderRadius={'8px'}
                color={'white'}
                textTransform={'capitalize'}
                alignSelf={'flex-end'}
                alignItems={'center'}
                justifyContent={'center'}
                fontSize={'12px'}
                fontFamily={'PlusJakartaSansBold'}
              >
                Add Employee
              </Button>
            </Flex>
          </Flex>
        </Flex>
        <Box
          minW={'100%'}
          w={'1px'}
          overflowX={'auto'}
          p={8}
          pt={0}
          bg={'#f8fbff'}
        >
          {adminsQuery.isLoading ? (
            <Text fontSize="lg">Loading Employees...</Text>
          ) : (
            <Table
              data={sorted}
              columns={columns({
                onViewAdmin: handleViewAdmin,
                onEditAdmin: handleEditAdmin,
                onDeleteAdmin: handleDeleteAdmin,
              })}
            />
          )}
        </Box>
        <AddAdminModal
          isOpen={isAddAdminOpen}
          onClose={onAddAdminClose}
        />
        <ViewAdminModal
          isOpen={isViewAdminOpen}
          onClose={onViewAdminClose}
          admin={selectedAdmin}
        />
        <EditAdminModal
          isOpen={isEditAdminOpen}
          onClose={onEditAdminClose}
          admin={selectedAdmin}
        />
        <DeleteAdminModal
          isOpen={isDeleteAdminOpen}
          onClose={onDeleteAdminClose}
          admin={selectedAdmin}
          onConfirm={confirmDeleteAdmin}
          isLoading={deleteAdminMutation.isPending}
        />
      </Box>
    </MainLayout>
  );
};

export default Users;
  